/**
 * apps/web/src/components/ui/Avatar.tsx
 *
 * Circular avatar. Shows the image when `src` is present and loads cleanly,
 * otherwise falls back to initials on the brand `accent` fill. A failed image
 * (404 / broken URL) transparently degrades to the initials rather than a
 * broken-image glyph.
 */

import type React from 'react';
import { useState } from 'react';
import { cn } from '../../lib/cn';

const SIZE: Record<'sm' | 'md' | 'lg', string> = {
  sm: 'h-7 w-7 text-xs',
  md: 'h-9 w-9 text-sm',
  lg: 'h-12 w-12 text-base',
};

interface AvatarProps {
  readonly name: string;
  readonly src?: string | null;
  readonly size?: 'sm' | 'md' | 'lg';
  readonly className?: string;
}

function initials(name: string): string {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length === 0) return '?';
  if (parts.length === 1) return (parts[0] ?? '?').slice(0, 2).toUpperCase();
  const first = (parts[0] ?? '').charAt(0);
  const second = (parts[1] ?? '').charAt(0);
  return `${first}${second}`.toUpperCase();
}

export function Avatar({ name, src, size = 'md', className }: AvatarProps): React.ReactElement {
  const [failed, setFailed] = useState(false);
  const base = cn('inline-flex shrink-0 items-center justify-center rounded-full', SIZE[size], className);

  if (src && !failed) {
    return (
      <img
        src={src}
        alt={name}
        className={cn(base, 'object-cover')}
        onError={() => setFailed(true)}
      />
    );
  }

  return (
    <span className={cn(base, 'bg-accent font-display font-semibold text-accent-foreground')} aria-hidden="true">
      {initials(name)}
    </span>
  );
}
